import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './components/auth/AuthContext';
import Login from './components/auth/Login/Login';
import Register from './components/auth/Register/Register';
import CompanyDashboard from './components/dashboard/CompanyDashboard';
import ProtectedRoute from './components/common/ProtectedRoute';
import ThemeRuntime from './components/common/ThemeRuntime';
import ResetPassword from './components/auth/ResetPassword/ResetPassword';
import VerifyEmail from './components/auth/VerifyEmail/VerifyEmail';
import ProfileSettings from './components/auth/Profile/ProfileSettings';

// Company Components
import CompanyProfile from './components/companies/CompanyProfile/CompanyProfile';
import CompanyInfo from './components/companies/CompanyProfile/CompanyInfo';
import CompanyTeam from './components/companies/CompanyTeam/CompanyTeam';
import CompanySettings from './components/companies/CompanySettings/CompanySettings';
import CompanyAnalytics from './components/companies/CompanyAnalytics/CompanyAnalytics';
import CompaniesListing from './components/companies/CompaniesListing/CompaniesListing';

// Meeting Components
import MeetingDashboard from './components/meetings/MeetingDashboard';
import ScheduleMeeting from './components/meetings/ScheduleMeeting';
import VideoCall from './components/meetings/VideoCall/VideoCall';

// Other Components
import UserDirectory from './components/users/UserDirectory';
import UserProfile from './components/users/UserProfile';
import Messages from './components/messages/Messages';
import NotificationCenter from './components/notifications/NotificationCenter';
import NotificationsPage from './components/notifications/NotificationsPage';
import Reports from './components/dashboard/Reporting';
import AdsManagement from './components/dashboard/AdsManagement';
import AIInsights from './components/dashboard/AIInsights';

// ✅ NEW: Support Chat Component
import SupportChat from './components/dashboard/SupportChat';

import './App.css';
import './styles/premium-enterprise.css';

function App() {
    console.log('🚀 App rendering');
    
    return (
        <AuthProvider>
            <ThemeRuntime>
            <Router>
                <Toaster 
                    position="top-right"
                    toastOptions={{
                        style: {
                            borderRadius: '12px',
                            background: 'rgba(255,255,255,.94)',
                            color: '#172033',
                            boxShadow: '0 14px 38px rgba(25,57,78,.14)',
                            border: '1px solid rgba(255,255,255,.8)',
                            backdropFilter: 'blur(14px)'
                        },
                        success: {
                            iconTheme: {
                                primary: '#10b981',
                                secondary: '#fff'
                            }
                        },
                        error: {
                            iconTheme: {
                                primary: '#d13a4f',
                                secondary: '#fff'
                            }
                        }
                    }}
                />
                <Routes>
                    {/* ============================================
                        AUTH ROUTES
                        ============================================ */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    
                    {/* ============================================
                        DASHBOARD
                        ============================================ */}
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <CompanyDashboard />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        SUPPORT CHAT - ✅ NEW ROUTE
                        ============================================ */}
                    <Route path="/dashboard/support" element={
                        <ProtectedRoute>
                            <SupportChat />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        ADS MANAGEMENT
                        ============================================ */}
                    <Route path="/dashboard/ads" element={
                        <ProtectedRoute>
                            <AdsManagement />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        AI INSIGHTS
                        ============================================ */}
                    <Route path="/dashboard/company/ai-insights" element={
                        <ProtectedRoute>
                            <AIInsights />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        NOTIFICATIONS PAGE
                        ============================================ */}
                    <Route path="/dashboard/notifications" element={
                        <ProtectedRoute>
                            <NotificationsPage />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/profile/settings" element={
                        <ProtectedRoute>
                            <ProfileSettings />
                        </ProtectedRoute>
                    } />

                    {/* Company users cannot access platform provider credentials. */}
{/* ============================================
                        COMPANY ROUTES
                        ============================================ */}
                    <Route path="/dashboard/company-info" element={
                        <ProtectedRoute>
                            <CompanyInfo />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/company/:id" element={
                        <ProtectedRoute>
                            <CompanyProfile />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/companies" element={
                        <ProtectedRoute>
                            <CompaniesListing />
                        </ProtectedRoute>
                    } />
                    <Route path="/dashboard/users" element={
                        <ProtectedRoute>
                            <UserDirectory />
                        </ProtectedRoute>
                    } />
                    <Route path="/dashboard/users/:id" element={
                        <ProtectedRoute>
                            <UserProfile />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/company/team" element={
                        <ProtectedRoute>
                            <CompanyTeam />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/company/settings" element={
                        <ProtectedRoute>
                            <CompanySettings />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/company/analytics" element={
                        <ProtectedRoute>
                            <CompanyAnalytics />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/company" element={
                        <ProtectedRoute>
                            <CompanyProfile />
                        </ProtectedRoute>
                    } />
                    <Route path="/dashboard/company/profile" element={
                        <ProtectedRoute>
                            <CompanyProfile />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        MEETING ROUTES
                        ============================================ */}
                    <Route path="/dashboard/meetings/schedule" element={
                        <ProtectedRoute>
                            <ScheduleMeeting />
                        </ProtectedRoute>
                    } />
                    
                    <Route path="/dashboard/meetings/room/:meetingId" element={
                        <ProtectedRoute>
                            <VideoCall />
                        </ProtectedRoute>
                    } />

                    <Route path="/dashboard/meetings" element={
                        <ProtectedRoute>
                            <MeetingDashboard />
                        </ProtectedRoute>
                    } />
                    
                    {/* Legacy Contacts route now opens the unified People directory. */}
                    <Route path="/dashboard/contacts" element={<Navigate to="/dashboard/users" replace />} />
                    
                    {/* ============================================
                        MESSAGE ROUTES
                        ============================================ */}
                    <Route path="/dashboard/messages" element={
                        <ProtectedRoute>
                            <Messages />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        NOTIFICATION CENTER (Keep this for backward compatibility)
                        ============================================ */}
                    <Route path="/dashboard/notifications/center" element={
                        <ProtectedRoute>
                            <NotificationCenter />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        REPORT ROUTES
                        ============================================ */}
                    <Route path="/dashboard/reports" element={
                        <ProtectedRoute>
                            <Reports />
                        </ProtectedRoute>
                    } />
                    
                    {/* ============================================
                        REDIRECTS
                        ============================================ */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </Router>
            </ThemeRuntime>
        </AuthProvider>
    );
}

export default App;